import { StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import "./index.css";
import i18n, { localePrefix, splitLocalePath } from "./i18n"; // Bilingual EN/AR — remove this line to roll back i18n
import { isMarketingPath, matchMarketingRoute } from "./marketing/routes";
import { MARKETING_PAGE_LOADERS } from "./marketing/pages";

// Observability + support widget + dogfood analytics — all no-op when env
// vars are unset, so this is safe to call unconditionally.
void Promise.all([
  import("./lib/sentry"),
  import("./lib/crisp"),
  import("./lib/ryze-pixel"),
]).then(([{ initSentry }, { initCrisp }, { initRyzePixel }]) => {
  initSentry();
  initCrisp();
  initRyzePixel();
});

const root = document.getElementById("root")!;
const { language, path } = splitLocalePath(window.location.pathname);

/**
 * Public marketing urls are served as prerendered html and run on a bundle of
 * their own: one page component, a router and the head manager, with none of
 * the application's providers. The url decides the language — an Arabic reader
 * arriving at /pricing gets the English page that url publishes, and the
 * language switcher moves them to /ar/pricing — so what the browser renders
 * always matches what was prerendered, and hydration keeps the served markup.
 */
async function mountMarketing() {
  await i18n.changeLanguage(language);

  const { route } = matchMarketingRoute(path);
  const { default: Page } = await MARKETING_PAGE_LOADERS[route.key]();
  const { default: MarketingApp } = await import("./marketing/MarketingApp");

  const tree = (
    <StrictMode>
      <BrowserRouter basename={localePrefix(language) || undefined}>
        <MarketingApp route={route} Page={Page} />
      </BrowserRouter>
    </StrictMode>
  );

  if (root.hasChildNodes()) {
    hydrateRoot(root, tree);
  } else {
    createRoot(root).render(tree);
  }
}

async function mountApp() {
  const [{ QueryCache, QueryClient, QueryClientProvider }, { default: App }] = await Promise.all([
    import("@tanstack/react-query"),
    import("./App.tsx"),
  ]);

  const queryClient = new QueryClient({
    // A query refused with `mfa_required` (e.g. a workspace just started requiring 2FA)
    // re-runs the two-factor gate so the code prompt appears instead of empty charts.
    queryCache: new QueryCache({
      onError: (error) => {
        if (/\bmfa_required\b/.test(String((error as { message?: unknown })?.message ?? ''))) {
          void import("./store/mfa-store").then((m) => m.useMfaStore.getState().check());
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        retry: 1,
      },
    },
  });

  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </StrictMode>,
  );
}

void (isMarketingPath(path) ? mountMarketing() : mountApp());
